import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/useAuth';
import api from '../utils/api';


const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth(); // Import from your context
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from?.pathname || "/dashboard";

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
           
            const response = await api.post('/auth/login', { email, password });
            // Use the context helper to update global state
            login(response.data.user, response.data.accessToken);
            if ((response.data.user.role === 'Executive') || (response.data.user.role === 'Board Member') || (response.data.user.role === 'System Administrator')) {
                navigate("/execdashboard", { replace: true });
            }
            else {
                navigate(from, { replace: true });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed. Please check your connection.');
        } finally {
            setLoading(false);
        } 
    };

    return (
        <div className="min-h-screen bg-slate-100 flex flex-col justify-center items-center px-4">
            
            {/* Portal Card */}
            <div className="w-full max-w-lg bg-white shadow-xl border border-slate-200">

                {/* Government Header */}
                <div className="bg-slate-800 text-white text-center px-8 py-6">
                    <div className="flex justify-center mb-3">
                        <img
                            src="/CountyLogo1.png"
                            alt="County Government Official Logo"
                            className="h-16 w-16 object-contain"
                        />
                    </div>

                    <h2 className="text-sm tracking-widest uppercase text-slate-300">
                        Embu County Government
                    </h2>

                    <h1 className="text-lg md:text-xl font-semibold mt-1">
                        Integrated County Revenue Management System (ICRMS)
                    </h1>

                    <p className="text-s text-slate-400 mt-2">
                        Revenue Performance Management & Reporting Portal
                    </p>
                </div>

                {/* Login Form */}
                <form onSubmit={handleLogin} className="px-8 py-8 space-y-6">

                    {error && (
                        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md flex items-start text-sm">
                            <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Email Field */}
                    <div>
                        <label 
                            htmlFor="email" 
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Official Email Address
                        </label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input
                                id="email"
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@eservices.embu.go.ke"
                                className="w-full border border-slate-300 rounded-md pl-10 pr-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-600 focus:border-slate-600 transition"
                            />
                        </div>
                    </div>

                    {/* Password Field */}
                    <div>
                        <label 
                            htmlFor="password" 
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Password
                        </label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-slate-400" size={18} />
                            <input
                                id="password"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                className="w-full border border-slate-300 rounded-md pl-10 pr-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-slate-600 focus:border-slate-600 transition"
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-slate-800 hover:bg-slate-700 text-white font-semibold py-3 rounded-md transition disabled:opacity-60"
                    >
                        {loading ? 'Authenticating…' : 'Sign In'}
                    </button>

                    {/* Security Notice */}
                    <div className="text-xs text-slate-500 text-center leading-relaxed border-t pt-4">
                        This is a secure government information system. Unauthorized access,
                        misuse, or modification of data is prohibited and may result in
                        disciplinary action and/or prosecution under applicable laws.
                    </div>
                </form>
            </div>

            {/* Footer */}
            <div className="mt-6 text-xs text-slate-500 text-center">
                © {new Date().getFullYear()} Embu County Government — Directorate of ICT
            </div>
        </div>
    );
};

export default Login;